import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import { Field, Label, Select } from '@shared/ui/Input';
import { useAssignKadryRequestMutation, useUnassignKadryRequestMutation } from '@shared/api';
import {
  KADRY_ASSIGNEE_ROLE_LABELS,
  kadryDate,
  type KadryAssigneeRole,
  type KadryHiringRequest,
  type KadryStaffDirectory,
} from '@entities/kadry';
import { KadryCardHead, KadryEmpty, KadryErrorBanner, KadryMuted, KadryPerson } from '../ui';

const List = styled.ul`
  list-style: none;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Who = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Remove = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.status.danger};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const AddForm = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

const Gap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

interface Props {
  request: KadryHiringRequest;
  staff: KadryStaffDirectory;
  /** Назначать и снимать может рекрутер/администратор с полным доступом. */
  canManage: boolean;
  onRefresh: () => void;
}

/** Назначенные на заявку рекрутеры и заказчики (открывают доступ к заявке). */
export function AssigneesPanel({ request, staff, canManage, onRefresh }: Props) {
  const [role, setRole] = useState<KadryAssigneeRole>('recruiter');
  const [userId, setUserId] = useState('');
  const [assign, assignState] = useAssignKadryRequestMutation();
  const [unassign, unassignState] = useUnassignKadryRequestMutation();

  const assigned = useMemo(() => new Set(request.assignees.map((a) => a.user_id)), [request.assignees]);
  const options: ComboboxOption[] = useMemo(
    () =>
      (role === 'recruiter' ? staff.recruiters : staff.requesters)
        .filter((s) => !assigned.has(s.user_id))
        .map((s) => ({ id: s.user_id, label: s.full_name, sublabel: s.department ?? undefined })),
    [role, staff.recruiters, staff.requesters, assigned],
  );
  const selectedLabel = options.find((o) => o.id === userId)?.label;
  const error = assignState.error ?? unassignState.error;
  const closed = request.status === 'closed';

  async function onAssign() {
    if (!userId) return;
    try {
      await assign({ id: request.id, user_id: userId, role }).unwrap();
      setUserId('');
    } catch {
      // Ошибка — в assignState.error.
    }
  }

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>Назначенные</CardTitle>
      </KadryCardHead>
      {request.assignees.length === 0 ? (
        <KadryEmpty>Никто не назначен.</KadryEmpty>
      ) : (
        <List>
          {request.assignees.map((a) => {
            const member = staff.byId.get(a.user_id);
            return (
              <Item key={a.user_id}>
                <Who>
                  <Name>
                    <KadryPerson id={a.user_id} />
                  </Name>
                  <Sub>
                    {member?.department ? `${member.department} · ` : ''}с {kadryDate(a.assigned_at)}
                  </Sub>
                </Who>
                <Badge $tone={a.role === 'recruiter' ? 'info' : 'neutral'}>
                  {KADRY_ASSIGNEE_ROLE_LABELS[a.role]}
                </Badge>
                {canManage && !closed && (
                  <Remove
                    type="button"
                    aria-label={`Снять назначение: ${staff.nameOf(a.user_id) ?? a.user_id}`}
                    title="Снять назначение"
                    disabled={unassignState.isLoading}
                    onClick={() => unassign({ id: request.id, user_id: a.user_id })}
                  >
                    ✕
                  </Remove>
                )}
              </Item>
            );
          })}
        </List>
      )}

      {error ? (
        <Gap>
          <KadryErrorBanner
            error={error}
            onRefresh={() => {
              assignState.reset();
              unassignState.reset();
              onRefresh();
            }}
            onDismiss={() => {
              assignState.reset();
              unassignState.reset();
            }}
          />
        </Gap>
      ) : null}

      {canManage && !closed && (
        <AddForm>
          <Field>
            <Label htmlFor="ka-role">Назначить</Label>
            <Select
              id="ka-role"
              value={role}
              onChange={(e) => {
                setRole(e.target.value as KadryAssigneeRole);
                setUserId('');
              }}
            >
              <option value="recruiter">Рекрутера</option>
              <option value="requester">Заказчика</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="ka-user">Сотрудник</Label>
            <Combobox
              id="ka-user"
              value={userId}
              selectedLabel={selectedLabel}
              options={options}
              onChange={(o) => setUserId(o?.id ?? '')}
              loading={staff.isLoading}
              placeholder="Начните вводить ФИО…"
              emptyText={
                role === 'recruiter'
                  ? 'Нет подходящих сотрудников с ролью рекрутера'
                  : 'Нет подходящих сотрудников с ролью заказчика'
              }
            />
          </Field>
          <Button
            type="button"
            $size="sm"
            $block
            disabled={!userId || assignState.isLoading}
            onClick={onAssign}
          >
            {assignState.isLoading ? 'Назначаем…' : 'Назначить'}
          </Button>
          <Gap />
          <KadryMuted>
            Назначение открывает сотруднику доступ к заявке: рекрутеру — полный, заказчику — без
            персональных данных кандидатов.
          </KadryMuted>
        </AddForm>
      )}
    </Card>
  );
}
